/**
 * Page-view tracking for the admin "user activity" monitor (ported from the old app's
 * useUserActivityTracker). One row per page visit with its duration; only `?view=` survives
 * from the query string (the aggregation SQL and the monitor apply the same rule).
 */
import { useEffect, useRef } from 'react'

const TRACK_URL = '/api/activity/track.php'
const SESSION_KEY = 'distribuai_user_activity_session_id'

function sessionId(): string {
    try {
        const existing = sessionStorage.getItem(SESSION_KEY)
        if (existing) return existing
        const created = `session_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
        sessionStorage.setItem(SESSION_KEY, created)
        return created
    } catch {
        return `session_${Date.now()}_ephemeral`
    }
}

function normalizeSearch(search: string): string {
    const view = new URLSearchParams(search).get('view')
    return view ? `?view=${view}` : ''
}

export function useActivityTracking({ enabled, pathname, search, routeKey, title }: { enabled: boolean; pathname: string; search: string; routeKey: string; title: string }) {
    const previous = useRef<string | null>(null)
    useEffect(() => {
        if (!enabled) return
        const startedAt = Date.now()
        const pagePath = `${pathname}${normalizeSearch(search)}`
        const referrer = previous.current
        let sent = false
        const send = () => {
            if (sent) return
            sent = true
            const elapsed = Date.now() - startedAt
            if (import.meta.env.DEV && elapsed < 100) return // StrictMode's synchronous remount
            // keepalive: the request must survive the page unloading. Tracking never blocks or throws.
            void fetch(TRACK_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
                keepalive: true,
                body: JSON.stringify({
                    page_path: pagePath,
                    route_key: routeKey,
                    page_title: title,
                    duration_seconds: Math.max(1, Math.round(elapsed / 1000)),
                    visited_at: new Date(startedAt).toISOString(),
                    session_id: sessionId(),
                    referrer_path: referrer,
                }),
            }).catch(() => undefined)
        }
        window.addEventListener('beforeunload', send)
        return () => {
            window.removeEventListener('beforeunload', send)
            send()
            previous.current = pagePath
        }
    }, [enabled, pathname, search, routeKey, title])
}
